/*!
 * © 2024. The EGT Universe. All Rights Reserved.
 * License: Requires ACTIVE AppArtisan Subscription.
 * For more information visit: https://egtuniverse.com/legal
 *
 * Created for Universe App Tools.
 *
 * Created by Justin K Kazmierczak.
 */
var _ = require("../../../uam/module.js")(exports, {
  namespace: "ua.tags",
  title: "Tags",
  control: true,
  description: "A tag selector that supports free-text entry, toggleable chips (≤50 options), or a removable-tag + dropdown UI (>50 options).",
  fields: {
    "name": {
      title: "Name",
      type: "string",
      description: "The field name used for form submission.",
      required: true
    },
    "title": {
      title: "Title",
      type: "string",
      description: "Label displayed above the tags.",
      required: true
    },
    "tags": {
      title: "Tags",
      type: "array",
      "of": "inner",
      description: "Optional list of predefined tags. Each item may be a string or { value, label }.",
      default: []
    },
    "required": {
      title: "Required",
      type: "boolean",
      description: "Whether at least one tag must be selected.",
      default: false
    }
  }
});

var jsonRender = require("../interface/jsonRender.js");
var uai = require("../interface/interface.js");

// ─── CSS (injected once) ──────────────────────────────────────────────────────

var _cssInjected = false;
function _injectCSS() {
  if (_cssInjected) return;
  _cssInjected = true;
  try {
    var style = document.createElement("style");
    style.textContent = [
      ".ua-tag-chip-label { cursor: pointer; user-select: none; }",
      ".ua-tag-chip-label input[type='checkbox'] { position: absolute; opacity: 0; width: 0; height: 0; }",
      ".ua-tag-chip {",
      "  display: inline-block;",
      "  padding: 0.35em 0.75em;",
      "  border-radius: 999px;",
      "  border: 1px solid #6c757d;",
      "  color: #6c757d;",
      "  font-size: 0.875em;",
      "  transition: background 0.15s, color 0.15s, border-color 0.15s;",
      "}",
      ".ua-tag-chip-label input:checked ~ .ua-tag-chip {",
      "  background-color: #0d6efd;",
      "  color: #fff;",
      "  border-color: #0d6efd;",
      "}",
      ".ua-tag-removable {",
      "  display: inline-flex;",
      "  align-items: center;",
      "  gap: 0.3em;",
      "  padding: 0.25em 0.6em;",
      "  border-radius: 999px;",
      "  background: #0d6efd;",
      "  color: #fff;",
      "  font-size: 0.875em;",
      "}",
      ".ua-tag-removable button {",
      "  background: none;",
      "  border: none;",
      "  color: #fff;",
      "  padding: 0;",
      "  line-height: 1;",
      "  cursor: pointer;",
      "  font-size: 1em;",
      "}"
    ].join("\n");
    document.head.appendChild(style);
  } catch (e) { /* ignore */ }
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function _normalizeTag(t) {
  if (!t) return null;
  if (typeof t === "string") return { value: t, label: t };
  return { value: (t.value || "").toString(), label: (t.label || t.value || "").toString() };
}

function _escapeHtml(str) {
  return (str || "").toString()
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// ─── Render ───────────────────────────────────────────────────────────────────

exports.render = async function(options) {
  var name = options.name || "";
  var title = options.title || "";
  var rawTags = options.tags || [];
  var isRequired = options.required === true || options.required === "true";

  _injectCSS();

  // Normalize tags list
  var tags = [];
  for (var t = 0; t < rawTags.length; t++) {
    var nt = _normalizeTag(rawTags[t]);
    if (nt) tags.push(nt);
  }

  var wrapId = uai.generateRandomID(12);

  // Wrapper element — this is the uae[name] control element
  var wrapper = document.createElement("div");
  wrapper.setAttribute("name", name);
  wrapper.className = "form-group pb-3 ua-tags-control";
  if (isRequired) wrapper.setAttribute("data-ua-tags-required", "true");

  // Label
  var labelEl = document.createElement("label");
  labelEl.className = "form-label fw-semibold";
  labelEl.textContent = title;
  wrapper.appendChild(labelEl);

  if (tags.length === 0) {
    // ── Free-text mode ──────────────────────────────────────────────
    _buildFreeText(wrapper, name, wrapId);
  } else if (tags.length <= 50) {
    // ── Chip/toggle mode ────────────────────────────────────────────
    _buildChips(wrapper, name, tags);
  } else {
    // ── Dropdown mode ───────────────────────────────────────────────
    _buildDropdown(wrapper, name, tags, wrapId);
  }

  return wrapper;
};

// ─── Free-text mode ───────────────────────────────────────────────────────────

function _buildFreeText(wrapper, name, wrapId) {
  var selectedHost = document.createElement("div");
  selectedHost.className = "d-flex flex-wrap gap-1 mb-2 ua-tags-selected";
  selectedHost.id = wrapId + "-selected";
  wrapper.appendChild(selectedHost);

  var inputGroup = document.createElement("div");
  inputGroup.className = "input-group input-group-sm";

  var textInput = document.createElement("input");
  textInput.type = "text";
  textInput.className = "form-control";
  textInput.placeholder = "Type a tag and press Enter or Add…";

  var addBtn = document.createElement("button");
  addBtn.type = "button";
  addBtn.className = "btn btn-outline-secondary";
  addBtn.textContent = "Add";

  inputGroup.appendChild(textInput);
  inputGroup.appendChild(addBtn);
  wrapper.appendChild(inputGroup);

  function addTag(val) {
    val = (val || "").toString().trim();
    if (!val) return;
    // Check for duplicates
    var existing = wrapper.querySelectorAll("input[type='hidden'][name='" + CSS.escape(name) + "']");
    for (var i = 0; i < existing.length; i++) {
      if (existing[i].value === val) return;
    }
    _addRemovableChip(selectedHost, name, val, val);
    textInput.value = "";
    textInput.focus();
  }

  addBtn.addEventListener("click", function() {
    addTag(textInput.value);
  });

  textInput.addEventListener("keydown", function(e) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag(textInput.value.replace(/,/g, ""));
    }
  });
}

// ─── Chip mode ────────────────────────────────────────────────────────────────

function _buildChips(wrapper, name, tags) {
  var chipList = document.createElement("div");
  chipList.className = "d-flex flex-wrap gap-2 mt-1 ua-tags-chip-list";

  for (var i = 0; i < tags.length; i++) {
    var tag = tags[i];
    var lbl = document.createElement("label");
    lbl.className = "ua-tag-chip-label m-0";

    var cb = document.createElement("input");
    cb.type = "checkbox";
    cb.name = name;
    cb.value = tag.value;

    var span = document.createElement("span");
    span.className = "ua-tag-chip";
    span.textContent = tag.label;

    lbl.appendChild(cb);
    lbl.appendChild(span);
    chipList.appendChild(lbl);
  }

  wrapper.appendChild(chipList);
}

// ─── Dropdown mode ────────────────────────────────────────────────────────────

function _buildDropdown(wrapper, name, tags, wrapId) {
  var selectedHost = document.createElement("div");
  selectedHost.className = "d-flex flex-wrap gap-1 mb-2 ua-tags-selected";
  selectedHost.id = wrapId + "-selected";
  wrapper.appendChild(selectedHost);

  var select = document.createElement("select");
  select.className = "form-select form-select-sm";

  var placeholder = document.createElement("option");
  placeholder.value = "";
  placeholder.textContent = "Add a tag…";
  select.appendChild(placeholder);

  for (var i = 0; i < tags.length; i++) {
    var opt = document.createElement("option");
    opt.value = tags[i].value;
    opt.textContent = tags[i].label;
    select.appendChild(opt);
  }

  wrapper.appendChild(select);

  // Build a label lookup for display
  var labelMap = {};
  for (var j = 0; j < tags.length; j++) {
    labelMap[tags[j].value] = tags[j].label;
  }

  select.addEventListener("change", function() {
    var val = select.value;
    var lbl = labelMap[val] || val;
    if (!val) return;
    // Avoid duplicates
    var existing = wrapper.querySelectorAll("input[type='hidden'][name='" + CSS.escape(name) + "']");
    for (var k = 0; k < existing.length; k++) {
      if (existing[k].value === val) {
        select.value = "";
        return;
      }
    }
    _addRemovableChip(selectedHost, name, val, lbl);
    select.value = "";
  });
}

// ─── Shared: removable chip + hidden input ────────────────────────────────────

function _addRemovableChip(host, name, value, label) {
  // Hidden input carries the value for _collectFields
  var hidden = document.createElement("input");
  hidden.type = "hidden";
  hidden.name = name;
  hidden.value = value;

  // Chip UI
  var chip = document.createElement("span");
  chip.className = "ua-tag-removable";

  var chipText = document.createElement("span");
  chipText.textContent = label;

  var removeBtn = document.createElement("button");
  removeBtn.type = "button";
  removeBtn.setAttribute("aria-label", "Remove " + label);
  removeBtn.innerHTML = "&times;";
  removeBtn.addEventListener("click", function() {
    host.removeChild(chip);
    if (hidden.parentNode) hidden.parentNode.removeChild(hidden);
  });

  chip.appendChild(chipText);
  chip.appendChild(removeBtn);

  host.appendChild(chip);
  // Insert hidden input after the host (inside the wrapper)
  if (host.parentNode) {
    host.parentNode.appendChild(hidden);
  }
}

// ─── Save ─────────────────────────────────────────────────────────────────────

async function save(name, control, repo) {
  var isRequired = control.getAttribute("data-ua-tags-required") === "true";
  var chipList = control.querySelector(".ua-tags-chip-list");
  var selected = [];

  if (chipList) {
    // Chip mode: collect checked checkboxes
    var cbs = chipList.querySelectorAll("input[type='checkbox']:checked");
    for (var i = 0; i < cbs.length; i++) {
      selected.push(cbs[i].value);
    }
  } else {
    // Free-text / dropdown mode: collect hidden inputs
    var hiddens = control.querySelectorAll("input[type='hidden'][name]");
    for (var j = 0; j < hiddens.length; j++) {
      if (hiddens[j].value) selected.push(hiddens[j].value);
    }
  }

  repo.errors = [];
  if (isRequired && selected.length === 0) {
    repo.errors.push({ type: "validation", message: name + " requires at least one tag." });
    repo.success = false;
    control.classList.add("is-invalid");
  } else {
    repo.success = true;
    control.classList.remove("is-invalid");
  }

  repo.data = selected;
  return repo;
}

// ─── Load ─────────────────────────────────────────────────────────────────────

async function load(name, control, data) {
  // Normalise data to an array of values
  var values = [];
  if (data instanceof Array) {
    values = data;
  } else if (typeof data === "string" && data.length > 0) {
    values = data.split(",").map(function(s) { return s.trim(); }).filter(Boolean);
  }

  var chipList = control.querySelector(".ua-tags-chip-list");

  if (chipList) {
    // Chip mode: check the matching checkboxes
    var cbs = chipList.querySelectorAll("input[type='checkbox']");
    for (var i = 0; i < cbs.length; i++) {
      cbs[i].checked = values.indexOf(cbs[i].value) >= 0;
    }
  } else {
    // Free-text / dropdown mode: remove existing hidden inputs and chips, re-add
    var selectedHost = control.querySelector(".ua-tags-selected");
    if (!selectedHost) return;

    // Remove existing hidden inputs tied to this name
    var existingHiddens = control.querySelectorAll("input[type='hidden'][name]");
    for (var j = 0; j < existingHiddens.length; j++) {
      existingHiddens[j].parentNode.removeChild(existingHiddens[j]);
    }
    selectedHost.innerHTML = "";

    // Rebuild from dropdown label map if available
    var labelMap = {};
    var selectEl = control.querySelector("select");
    if (selectEl) {
      var opts = selectEl.querySelectorAll("option");
      for (var k = 0; k < opts.length; k++) {
        if (opts[k].value) labelMap[opts[k].value] = opts[k].textContent;
      }
    }

    for (var m = 0; m < values.length; m++) {
      var val = values[m];
      var lbl = labelMap[val] || val;
      _addRemovableChip(selectedHost, name, val, lbl);
    }
  }
}

module.exports.save = save;
module.exports.load = load;
